import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router';
import './index.css';
import './lib/i18n'; // Initialize i18n
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AgeVerificationGate from './components/AgeVerificationGate';
import { ROUTES, ROUTE_PATTERNS } from './lib/constants';
import { usePageTracking } from './hooks/usePageTracking';

// ページ遷移追跡コンポーネント
function PageTracker({ children }: { children: React.ReactNode }) {
    usePageTracking();
    return <>{children}</>;
}
// Home.tsx is no longer used - landing page is now WatchIndex
// import Home from './routes/Home';
import Studio from './routes/Studio';
// Login.tsx is no longer used - AdminLogin handles admin/streamer, ViewerLogin handles viewers
// import Login from './routes/Login';
import AdminLogin from './routes/AdminLogin';
import ViewerLogin from './routes/ViewerLogin';
import Register from './routes/Register';
import { CompanyPage, LawPage, PolicyPage, PrivacyPage, TermsPage } from './routes/LegalPages';
import VerifyEmail from './routes/VerifyEmail';
import ForgotPassword from './routes/ForgotPassword';
import ResetPassword from './routes/ResetPassword';
import Console from './routes/Console';
import PublishOme from './routes/PublishOme';
import WatchIndex from './routes/watch._index';
import WatchStreamerId from './routes/watch.$streamerId';
import WatchBeforeEntry from './routes/watch.$streamerId.before-entry';
import WatchLiveRoom from './routes/watch.$streamerId.live-room';
import WatchPeekRoom from './routes/watch.$streamerId.peek-room';
import Points from './routes/Points';
import Settings from './routes/Settings';
import StaffSettings from './routes/StaffSettings';
import StreamerMyPage from './routes/StreamerMyPage';
import Footer from './components/Footer';
import Guide from './routes/Guide';

createRoot(document.getElementById('root')!).render(
    <StrictMode>
        <AuthProvider>
            <BrowserRouter>
                <AgeVerificationGate>
                    <PageTracker>
                    <>
                    <Routes>
                    {/* Public routes - no authentication required */}
                    {/* Landing page is now the stream list (WatchIndex) */}
                    <Route path={ROUTES.HOME} element={<WatchIndex />} />
                    <Route path={ROUTES.LOGIN} element={<ViewerLogin />} />
                    <Route path={ROUTES.STAFF_LOGIN} element={<AdminLogin />} />
                    <Route path={ROUTES.REGISTER} element={<Register />} />
                    <Route path={ROUTES.GUIDE} element={<Guide />} />
                    <Route path={ROUTES.TERMS} element={<TermsPage />} />
                    <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />
                    <Route path={ROUTES.LAW} element={<LawPage />} />
                    <Route path={ROUTES.POLICY} element={<PolicyPage />} />
                    <Route path={ROUTES.COMPANY} element={<CompanyPage />} />
                    <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />
                    <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
                    <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />

                    {/* Protected routes - authentication required */}
                    <Route
                        path={ROUTES.STUDIO}
                        element={
                            <ProtectedRoute>
                                <Studio />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={ROUTES.CONSOLE}
                        element={
                            <ProtectedRoute requireAdmin>
                                <Console />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={ROUTES.PUBLISH_OME}
                        element={
                            <ProtectedRoute>
                                <PublishOme />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={ROUTES.STREAMER_MYPAGE}
                        element={
                            <ProtectedRoute>
                                <StreamerMyPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* Public watch routes - no authentication required for viewing */}
                    {/* Authentication is handled within components for room entry */}
                    <Route path={ROUTES.WATCH} element={<WatchIndex />} />
                    <Route path={ROUTE_PATTERNS.WATCH_STREAMER} element={<WatchStreamerId />} />
                    <Route path={ROUTE_PATTERNS.WATCH_BEFORE_ENTRY} element={<WatchBeforeEntry />} />
                    <Route path={ROUTE_PATTERNS.WATCH_LIVE_ROOM} element={<WatchLiveRoom />} />
                    <Route path={ROUTE_PATTERNS.WATCH_PEEK_ROOM} element={<WatchPeekRoom />} />
                    <Route
                        path={ROUTES.POINTS}
                        element={
                            <ProtectedRoute loginPath={ROUTES.LOGIN}>
                                <Points />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={ROUTES.SETTINGS}
                        element={
                            <ProtectedRoute loginPath={ROUTES.LOGIN}>
                                <Settings />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={ROUTES.STAFF_SETTINGS}
                        element={
                            <ProtectedRoute loginPath={ROUTES.STAFF_LOGIN}>
                                <StaffSettings />
                            </ProtectedRoute>
                        }
                    />
                </Routes>
                <Footer />
                </>
                    </PageTracker>
                </AgeVerificationGate>
            </BrowserRouter>
        </AuthProvider>
    </StrictMode>
);
